import _ from 'lodash';
import {
  createResource,
  readEndpoint,
  updateResource,
  deleteResource,
} from '@keypr/redux-json-api';
// import Promise from 'bluebird';
import { apiRequest } from '@keypr/redux-json-api/lib/utils';
import settings from '../../config/settings';
import urls from '../../config/api-urls.json';
import {
  API_UPLOADED,
} from '../types';

const API_URL = `${settings.gems_url}`;

const getEndpointHeaders = function getEndpointHeaders(cfg) {
  let endpoint;
  let headers = {};
  let wrapData = true;
  const config = cfg;
  if (!_.has(cfg, 'urlParams')) {
    config.urlParams = {};
  }
  if (_.has(config, 'type') && _.has(urls, config.type)) {
    const urlSettings = urls[config.type];
    endpoint = `${_.template(urlSettings.endpoint)(config.urlParams || {})}`;
    headers = urlSettings.headers || {};
  } else {
    throw new Error(`Missing url type configuration: '${config.type}'.`);
  }
  if (_.has(config.urlParams, 'page') === true && config.urlParams.page) {
    let sep = '?';
    if (endpoint.indexOf(sep) !== -1) {
      sep = '&';
    }
    endpoint = `${endpoint}${sep}page=${config.urlParams.page}`;
  }
  // @TODO: Remove this when kcsticket is rewritten
  if (endpoint.substring(0, 6) === 'ticket') {
    wrapData = false;
  }
  return { endpoint, headers, wrapData };
};

const apiCreate = function apiCreate(config) {
  return function apiCreateFunction(dispatch) {
    if (!_.has(config, 'data')) {
      throw new Error('Missing data.');
    }
    const data = config.data;
    const { endpoint, headers, wrapData } = getEndpointHeaders(config);
    const options = { urlParams: config.urlParams || {}, configType: config.type };
    return dispatch(createResource(data, { endpoint, headers, wrapData, options }));
  };
};

const apiRead = function apiRead(cfg) {
  return function apiReadFunction(dispatch) {
    const config = (typeof cfg === 'string') ? { type: cfg, urlParams: {}, options: {} } : cfg;
    const { endpoint, headers, wrapData } = getEndpointHeaders(config);
    const options = { urlParams: config.urlParams || {}, configType: config.type };
    if (_.has(config, 'options')) {
      if (_.has(config.options, 'retries')) {
        options.retries = config.options.retries;
      }
      if (_.has(config.options, 'retryDelay')) {
        options.retryDelay = config.options.retryDelay;
      }
    }

    if (_.has(config, 'noLoader')) {
      options.noLoader = config.noLoader;
    }

    return dispatch(readEndpoint(endpoint, { headers, wrapData, options }));
  };
};

const apiUpdate = function apiUpdate(config) {
  return function apiUpdateFunction(dispatch) {
    if (!_.has(config, 'data')) {
      throw new Error('Missing data.');
    }
    const { endpoint, headers, wrapData } = getEndpointHeaders(config);
    const data = config.data;
    const options = { urlParams: config.urlParams || {}, configType: config.type };
    return dispatch(updateResource(data, { endpoint, headers, wrapData, options }));
  };
};

const apiDelete = function apiDelete(config) {
  return function apiDeleteFunction(dispatch) {
    if (!_.has(config, 'data')) {
      throw new Error('Missing data. Requires data.id & data.type.');
    }
    if (!_.has(config.data, 'id')) {
      throw new Error('Missing id.');
    }
    if (!_.has(config.data, 'type')) {
      throw new Error('Missing type.');
    }
    const { endpoint, headers, wrapData } = getEndpointHeaders(config);
    const data = config.data;
    const options = { urlParams: config.urlParams || {}, configType: config.type };
    return dispatch(deleteResource(data, { endpoint, headers, wrapData, options }));
  };
};

const apiPut = function apiPut(cfg, opts) {
  const config = (typeof cfg === 'string') ? { type: cfg, urlParams: {}, options: {} } : cfg;
  const { endpoint, headers, wrapData } = getEndpointHeaders(config);

  return (dispatch, getState) => {
    const {
      api: {
        endpoint: {
          headers: {
            Authorization,
          },
        },
      },
    } = getState();
    return new Promise((resolve, reject) => {
      const options = Object.assign({}, opts, {
        method: 'PUT',
        headers: Object.assign({}, headers, {
          Authorization,
        }),
        credentials: 'include',
        wrapData,
      });
      apiRequest(`${API_URL}/${endpoint}`, options)
        .then((json) => {
          resolve(json);
        })
        .catch((error) => {
          const err = error;
          err.endpoint = endpoint;
          reject(err);
        });
    });
  };
};

const apiFetch = function apiFetch(cfg, opts) {
  const config = (typeof cfg === 'string') ? { type: cfg, urlParams: {}, options: {} } : cfg;
  const { endpoint, headers, wrapData } = getEndpointHeaders(config);

  return (dispatch, getState) => {
    const {
      api: {
        endpoint: {
          headers: {
            Authorization,
          },
        },
      },
    } = getState();
    return new Promise((resolve, reject) => {
      const options = Object.assign({}, opts, {
        headers: Object.assign({}, headers, {
          Authorization,
        }),
        credentials: 'include',
        wrapData,
      });
      apiRequest(`${API_URL}/${endpoint}`, options)
        .then((json) => {
          resolve(json);
        })
        .catch((error) => {
          const err = error;
          err.endpoint = endpoint;
          reject(err);
        });
    });
  };
};

const apiUpload = function apiUpload(cfg, name, file) {
  return function apiUploadFunction(dispatch, getState) {
    const config = (typeof cfg === 'string') ? { type: cfg, urlParams: {}, options: {} } : cfg;
    const { endpoint, headers } = getEndpointHeaders(config);
    const { api: { endpoint: {
      host: apiHost,
      path: apiPath,
      headers: {
        Authorization,
      },
    } } } = getState();

    const data = new window.FormData();
    data.append(name, file);
    const opts = {
      method: 'POST',
      body: data,
    };

    return new Promise((resolve, reject) => {
      const options = Object.assign({}, opts, {
        headers: Object.assign({}, headers, {
          Authorization,
        }),
      });
      apiRequest(`${apiHost}${apiPath}/${endpoint}`, options)
        .then((json) => {
          dispatch({
            type: API_UPLOADED,
            payload: { resource: json, endpoint, options },
          });
          resolve(json);
        })
        .catch((error) => {
          const err = error;
          err.resource = file;
          err.endpoint = endpoint;
          reject(err);
        });
    });
  };
};

export default {
  apiCreate,
  apiRead,
  apiUpdate,
  apiDelete,
  apiUpload,
  apiFetch,
  apiPut,
};



// WEBPACK FOOTER //
// ./src/state/actions/crud.js